import React, { MouseEventHandler, useCallback, useEffect, useMemo } from 'react';
import { Btn } from 'components/btn/Btn';
import { ArrowUpRight } from 'react-feather';
import { DealUIObject, getDealsFromAffiliateUrl } from 'common/api/deals/getDealsByLinkId';
import { Brand } from 'common/features/store/duck/search-generic/utils/searchResultBrandsToBrands';
import { formatDuration, msToDays, msUntil } from 'common/utils/time';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTicket, faTimer } from '@fortawesome/pro-regular-svg-icons';
import { useGetFlipgiveToken } from 'common/hooks/useGetAffiliateActivationUrl';

export interface DealsEarningTextHeaderProps {
    brand: Brand;
}

export const DealsBonusExpiration = ({ expiration }: { expiration: string }) => {
    if (!expiration) {
        return null;
    }

    const timeRemaining = msUntil(expiration);

    if (msToDays(timeRemaining) > 7) {
        return null;
    }

    return (
        <span className="sm:pt-0 pt-1 sm:text-right text-center font-semibold sm:mr-3 text-xs text-bonus-pink flex-wrap">
            {formatDuration(timeRemaining)} left
        </span>
    );
};

export const DealsEarningTextHeader = ({ brand }: DealsEarningTextHeaderProps) => {
    return (
        <div className="flex flex-col justify-between">
            <div className="flex sm:mr-3 items-baseline">
                <span className="text-darkest-gray font-[14px] font-normal">
                    {brand.affiliateVariablePricingText ? 'Earn up to' : 'Earn'}
                </span>
                <span>&nbsp;</span>

                {brand.isAffiliateOfferOnBonus && brand.affiliateBonus && (
                    <span className="line-through pr-1"> {brand.affiliateRebate}%</span>
                )}

                <span
                    className={`${brand.isAffiliateOfferOnBonus && brand.affiliateBonus
                        ? 'text-bonus-pink'
                        : 'text-dark-blue'
                        } font-semibold text-[18px]`}
                >
                    {brand.isAffiliateOfferOnBonus && brand.affiliateBonus
                        ? `${brand.affiliateBonus}% +`
                        : `${brand.affiliateRebate}% +`}
                </span>
            </div>

            {brand.isAffiliateOfferOnBonus && brand.affiliateBonus && brand.affiliateBonusEnd ? (
                <DealsBonusExpiration expiration={brand.affiliateBonusEnd} />
            ) : null}
        </div>
    );
};
export interface DealsProps {
    affiliateUrl: string;
    earningsText: React.ReactNode;
    onDealActivated?: (dealType: 'deal' | 'coupon') => void;
}

export const CouponButton = ({
    couponCode,
    onClick,
}: {
    couponCode: string;
    onClick: MouseEventHandler<HTMLButtonElement>;
}) => {
    return (
        <button
            className="border border-brand border-dashed flex flex-row items-center"
            onClick={onClick}
        >
            <FontAwesomeIcon icon={faTicket} className="text-brand pl-4 pr-2" />

            <div className="text-brand py-1 pr-4 text-[13px] font-semibold">{couponCode}</div>
        </button>
    );
};

export const Deals = ({ affiliateUrl, earningsText, onDealActivated }: DealsProps) => {
    const [deals, setDeals] = React.useState<DealUIObject[]>([]);
    const [couponHasBeenClicked, setCouponHasBeenClicked] = React.useState<Record<string, boolean>>(
        {}
    );
    const [lastClickedCoupon, setLastClickedCoupon] = React.useState<number>(-1);
    const [showMore, setShowMore] = React.useState<boolean>(true);

    const {token, isLoading: isLoadingToken } = useGetFlipgiveToken();

    const renderedDeals = useMemo(() => {
        return showMore ? deals : deals.slice(0, 2);
    }, [deals, showMore]);

    const openUrlWithParams = useCallback((url: string, dealType: 'deal' | 'coupon' = 'deal') => {
        const urlWithUTMs = new URL(url);
        urlWithUTMs.searchParams.set('utm_channel', 'Web');
        urlWithUTMs.searchParams.set('utm_source', 'pdp');
        urlWithUTMs.searchParams.set('utm_campaign', dealType);
        window.open(urlWithUTMs.toString(), '_blank', 'noopener,noreferrer');

        onDealActivated?.(dealType);
    }, [onDealActivated]);

    const openDeal = useCallback((deal: DealUIObject) => {
        const url = deal?.url || affiliateUrl;

        if (url) {
            openUrlWithParams(url, deal.dealType);
        }
    }, [affiliateUrl, openUrlWithParams]);

    const delayedWriteToClipboardAndNavigate = useCallback(
        (deal: DealUIObject, dealIndex: number) => {
            const couponCode = deal.couponCode;
            const url = deal.url || affiliateUrl;

            let timer: null | NodeJS.Timeout = null;

            if (couponCode && url) {
                timer = setTimeout(() => {
                    openDeal(deal);
                    setLastClickedCoupon(-1);
                }, 2000);
            }

            if (couponCode) {
                setLastClickedCoupon(dealIndex);
                navigator.clipboard.writeText(couponCode);
            }

            return () => {
                if (timer) {
                    clearTimeout(timer);
                }
            };
        },
        [affiliateUrl, openDeal]
    );

    const handleDealClicked = useCallback(
        (deal: DealUIObject, dealIndex: number) => {
            if (deal.couponCode) {
                setCouponHasBeenClicked({
                    ...couponHasBeenClicked,
                    [dealIndex]: true,
                });

                delayedWriteToClipboardAndNavigate(deal, dealIndex);
            } else {
                openDeal(deal);
            }
        },
        [setLastClickedCoupon, couponHasBeenClicked, setCouponHasBeenClicked, openDeal]
    );

    useEffect(() => {
        if (!affiliateUrl || isLoadingToken || !token) {
            setDeals([]);
            setShowMore(true); // todo this naming is weird
            return;
        }

        getDealsFromAffiliateUrl(affiliateUrl, token).then((response: DealUIObject[]) => {
            setDeals(response);
            setShowMore(response.length < 3);
        });
    }, [affiliateUrl, token, isLoadingToken]);

    if (!deals || deals.length === 0) {
        return null;
    }

    return (
        <>
            <h2 className="text-brand-dark text-[20px] font-semibold pb-[16px] pt-[32px]">
                Top Online Deals &amp; Coupons
            </h2>

            <div className="bg-pale-blue p-[20px] pb-[25px]">
                {renderedDeals.map((deal, dealIndex) => {
                    return (
                        <div
                            className="bg-white py-[16px] px-[24px] flex flex-col md:flex-row justify-between items-center rounded-[8px] mb-[12px] "
                            key={'' + deal.title + deal.couponCode + deal.header + dealIndex}
                        >
                            <div className="md:w-[430px] w-full flex-grow-1 items-start md:items-center">
                                <div className="flex md:flex-row justify-between items-baseline">
                                    <div>{earningsText}</div>

                                    {deal.expirationMessage && (
                                        <div>
                                            <span className="text-warning-orange pr-[4px] w-[10px] h-[10px]">
                                                <FontAwesomeIcon icon={faTimer} />
                                            </span>

                                            <span className="text-warning-orange text-[12px] font-semibold">
                                                {deal.expirationMessage}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                <div className="text-darkest-gray font-[16px] font-semibold pb-[8px] pt-[6px]">
                                    {deal.title}
                                </div>
                            </div>

                            <div className="flex flex-row items-start sm:items-center w-full md:w-auto flex-shrink mt-[8px]">
                                {deal.couponCode && couponHasBeenClicked[dealIndex] ? (
                                    <div className="flex flex-row items-center justify-center md:flex-col md:relative">
                                        <CouponButton
                                            couponCode={deal.couponCode}
                                            onClick={() => handleDealClicked(deal, dealIndex)}
                                        />
                                        {lastClickedCoupon === dealIndex && (
                                            <div className="text-success font-semibold font-[12px] ml-1 md:ml-0 md:absolute md:top-[32px]">
                                                <FontAwesomeIcon
                                                    icon={faCheck}
                                                    className="text-success ml-2 font-semibold font-[12px]"
                                                />{' '}
                                                Copied
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <Btn
                                        isSecondary
                                        isSmall
                                        onClick={() => handleDealClicked(deal, dealIndex)}
                                    >
                                        <span className="flex flex-row flex-nowrap whitespace-nowrap items-start font-semibold font-size-[13px] py-[2px] pl-2">
                                            <span className="pr-[4px]">{deal.ctaLabel}</span>{' '}
                                            <ArrowUpRight
                                                className="inline-block"
                                                width="15"
                                                height="15"
                                            />
                                        </span>
                                    </Btn>
                                )}
                            </div>
                        </div>
                    );
                })}

                {deals.length > 2 && (
                    <div className="flex flex-row justify-center">
                        {!showMore ? (
                            <a
                                className="text-brand font-semibold text-[13px] cursor-pointer"
                                onClick={() => setShowMore(true)}
                            >
                                Show More
                            </a>
                        ) : (
                            <a
                                className="text-brand font-semibold text-[13px] cursor-pointer"
                                onClick={() => setShowMore(false)}
                            >
                                Show Less
                            </a>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};
